import React, { SetStateAction, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { IStore } from "redux/store";
import { TCNRColumn, TCNRColumnsIDs } from "redux/app/vessel/onboard/types";
import classNames from "classnames";
import { Dispatch } from "redux";
import { logActions } from "redux/app/logs";
import { getSelectedDate } from "redux/app/tool/utils";
import StickyColumns from "../_elements/StickyColumns";
import moment from "moment";
import ModalPortal from "components/app/_common/ModalPortal";
import SOFTable from "../_elements/SOFTable";

interface IReportRowProps {
	report: any;
	indexNumber: number;
	onClick: (report: any) => void;
	selectedColumns: TCNRColumn[];
	isSignalStatusEnabled?: boolean;
	isIMOSStatusEnabled?: boolean;
	isDocStatusEnabled?: boolean;
	setStatusModalOpen: React.Dispatch<SetStateAction<boolean>>;
	setErrorDetails: React.Dispatch<SetStateAction<any>>;
	setIsToolsModalOpen: React.Dispatch<SetStateAction<boolean>>;
	setRemarksModal: React.Dispatch<SetStateAction<string>>;
	stickyOffsets: Record<string, { sm: number; lg: number }>;
}

const ReportRow: React.FC<IReportRowProps & PropsFromRedux> = (props) => {
	const {
		report,
		selectedColumns,
		isSignalStatusEnabled,
		isIMOSStatusEnabled,
		setStatusModalOpen,
		setErrorDetails,
		setIsToolsModalOpen,
		setRemarksModal,
		isDocStatusEnabled,
		stickyOffsets,
	} = props;

	const [isSOFTableOpen, setIsSOFTableOpen] = useState<"statementOfFactsTable" | "cargoHandlingTable" | "">("");

	const ignoredColumns = [
		"selectedDate",
		"createdAt",
	]

	const hasInvalidItems = report?.timeline?.some((item: any) => {
		// Skip validation for embedded cargo reports
		if (item?.report_type === "DEPARTURE" && item?.cargoReport && Array.isArray(item?.cargoReport)) {
			// This is a departure report with embedded cargo reports, check only the departure report itself
			return item?.isValid === false;
		}
		return item?.isValid === false;
	});

	const hasUnsetItems = report?.timeline?.some((item: any) => {
		// Skip validation for embedded cargo reports
		if (item?.report_type === "DEPARTURE" && item?.cargoReport && Array.isArray(item?.cargoReport)) {
			// This is a departure report with embedded cargo reports, check only the departure report itself
			return item?.isValid === undefined || item?.isValid === null;
		}
		return item?.isValid === undefined || item?.isValid === null;
	});
	const getRowClassName = () => {
		const baseClass = "my-1 border hover:cursor-pointer";
		if (hasInvalidItems) {
			return `${baseClass} bg-red-50 hover:bg-red-100`;
		} else if (hasUnsetItems) {
			return `${baseClass} bg-yellow-50 hover:bg-yellow-100`;
		} else {
			return `${baseClass} hover:bg-gray-200`;
		}
	};
	const getColumnClassnames = (condition: string) => {
		switch (condition) {
			case "statementOfFactsTable":
				return "text-blue-500 bg-blue-100 px-1 py-0.5 border border-blue-500 rounded-full"
			case "cargoHandlingTable":
				return "text-blue-500 bg-blue-100 px-1 py-0.5 border border-blue-500 rounded-full"
			default:
				return "text-gray-500 bg-gray-100 px-1 py-0.5 border border-gray-500 rounded-full"
		}
	}

	const totalSubRows = 1;
	const reportType = report?.standaloneForm?.statementOfFacts?.report_type as keyof TCNRColumnsIDs;
	const reportItem = report?.standaloneForm?.statementOfFacts;
	return (
		<>
			<tr
				key={report?._id}
				className={getRowClassName()}
			>
				<td rowSpan={totalSubRows} className="border-r text-center text-gray-500 px-0.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-xs lg:text-sm">
					{props.indexNumber}
				</td>
				{selectedColumns?.find(item => item?.ids?.STATEMENT_OF_FACTS === "selectedDate") ?
					<td className="text-center font-medium text-gray-800 px-0.5 py-0.5 md:px-2 md:py-4 text-[9px] md:text-xs lg:text-sm">
						<div className="flex flex-col">
							<span>{getSelectedDate(report)}</span>
						</div>
					</td>
					: null
				}
				{selectedColumns?.find(item => item?.ids?.STATEMENT_OF_FACTS === "createdAt") ?
					<td
						className="border-r font-medium text-center text-gray-700 px-0.5 py-0.5 md:px-2 md:py-1 text-[6px] md:text-xs lg:text-sm"
						title={moment(report.createdAt).format("DD-MM-YYYY hh:mm a")}
					>
						<p>{moment.utc(report?.createdAt).format("DD-MM-YYYY")}</p>
						<p>{moment.utc(report?.createdAt).format("hh:mm a")}</p>
					</td>
					: null}
				{selectedColumns?.filter(item => !ignoredColumns?.includes(item?.ids?.[reportType] || ""))?.map(column => {
					if (column?.children) {
						return column.children?.map((child: any) => {
							const columnId = child?.ids?.[reportType];
							return (
								<td key={columnId} className="text-center text-gray-800 px-0.5 py-0.5 md:px-2 md:py-4 text-[9px] md:text-xs lg:text-sm">
									{reportItem?.[columnId] ?? "-"}
								</td>
							)
						})
					}

					const columnId = column?.ids?.[reportType];
					if (columnId === "sof_eosp_date") {
						return (
							<td
								key={columnId}
								className="text-center font-medium text-gray-700 px-0.5 py-0.5 md:px-2 md:py-1 text-[6px] min-w-48 md:text-xs lg:text-sm"
							>
								{(reportItem?.sof_eosp_date) ?
									<div className="flex gap-2 justify-around">
										<div className="flex-col flex items-start">
											<div className="flex gap-2 items-end">
												{reportItem?.sof_eosp_date}
												{reportItem?.sof_eosp_time &&
													<>
														<span className="text-gray-500 text-xs">at</span>
														<div className="flex gap-2">
															<span className="">
																{reportItem?.sof_eosp_time}
															</span>
														</div>
													</>
												}
											</div>
											{reportItem?.sof_eosp_zd &&
												<span className="text-gray-500">({reportItem?.sof_eosp_zd})</span>
											}
										</div>
									</div>
									: "-"
								}
							</td>
						)
					}
					if (columnId === "sof_port_details_date") {
						return (
							<td
								key={columnId}
								className=" text-center font-medium text-gray-700 px-0.5 py-0.5 md:px-2 md:py-1 text-[6px] min-w-48 md:text-xs lg:text-sm"
							>
								{(reportItem?.sof_port_details_date) ?
									<div className="flex gap-2 justify-around">
										<div className="flex-col flex items-start">
											<div className="flex gap-2 items-end">
												{reportItem?.sof_port_details_date}
												{reportItem?.sof_port_details_time &&
													<>
														<span className="text-gray-500 text-xs">at</span>
														<div className="flex gap-2">
															<span className="">
																{reportItem?.sof_port_details_time}
															</span>
														</div>
													</>
												}
											</div>
											{reportItem?.sof_port_details_zd &&
												<span className="text-gray-500">({reportItem?.sof_port_details_zd})</span>
											}
										</div>
									</div>
									: "-"
								}
							</td>
						)
					}

					if (columnId === "statementOfFactsTable" || columnId === "cargoHandlingTable") {
						return (
							<td
								key={columnId}
								className={"text-center font-medium text-gray-800 px-0.5 py-0.5 md:px-2 md:py-4 text-[9px] md:text-xs lg:text-sm"}
							>
								<button
									className={classNames(
										"w-full",
										getColumnClassnames(columnId),
									)}
									onClick={() => setIsSOFTableOpen(columnId)}
								>
									Show Table
								</button>
							</td>
						)
					}

					return (
						<td 
							key={columnId}
							className="text-center font-medium text-gray-800 px-0.5 py-0.5 md:px-2 md:py-4 text-[9px] md:text-xs lg:text-sm"
							onClick={() => {
								if (!columnId || !reportItem?.[columnId]) return;
								if (column.label === "Remarks") {
									setRemarksModal(reportItem?.[columnId])
								}
							}}
						>
							{columnId && reportItem?.[columnId] ?
								<div className={classNames(
									column.label === "Remarks" ? "w-96" : null
								)}>
									<span>
										{column.label === "Remarks" ?
											<>
												{reportItem?.[columnId]?.slice(0, 50)}
												{reportItem?.[columnId]?.length >= 50 ?
													<>
														<span
															className="cursor-pointer text-gray-600 text-lg"
														>
															...
														</span>
													</>
													: null}
											</>
											: reportItem?.[columnId]
										}
									</span>
								</div>
								: "-"
							}
						</td>
					)
				})}
				<StickyColumns
					report={report}
					reportItem={reportItem}
					reportType={reportType}
					isFirstRow={true}
					totalSubRows={totalSubRows}
					isSignalStatusEnabled={isSignalStatusEnabled}
					isIMOSStatusEnabled={isIMOSStatusEnabled}
					isDocStatusEnabled={isDocStatusEnabled}
					stickyOffsets={stickyOffsets}
					setStatusModalOpen={setStatusModalOpen}
					setErrorDetails={setErrorDetails}
					onClickView={() => {
						props.onClick(report);
					}}
					onClickTools={() => {
						props.loadReport(report._id)
						setIsToolsModalOpen(true);
					}}
				/>
			</tr>
			{isSOFTableOpen ?
				<ModalPortal
					isActive={!!isSOFTableOpen}
					onClose={() => setIsSOFTableOpen("")}
				>
					<SOFTable
						reportItem={reportItem}
						tableType={isSOFTableOpen}
						onClose={() => setIsSOFTableOpen("")}
					/>
				</ModalPortal>
				: null
			}
		</>
	);
}

function mapStateToProps(store: IStore, ownProps: IReportRowProps) {
	return {};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		loadReport(reportId: string) {
			dispatch(logActions.commands.singleReportLoad(reportId));
		},
	};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(ReportRow);